import { createContext, useContext } from 'react';

/**
 * Default string values when `FplSettings.StringProvider` is not used.
 * @sort alpha
 */
const DEFAULT_STRINGS = {
    back: () => 'Back',
    clearSearch: () => 'Clear search',
    close: () => 'Close',
    danger: () => 'Danger:',
    dismiss: () => 'Dismiss',
    error: () => 'Error:',
    fraction: (opts) => `${opts.value} out of ${opts.max}`,
    fractionWithUnits: (opts) => `${opts.value} ${opts.unit} out of ${opts.max}`,
    hasChecked: () => 'Contains checked item',
    help: () => 'Help',
    loading: () => 'Loading',
    mediaElapsedOfTotal: (opts) => `${opts.elapsed} of ${opts.total}`,
    mediaElapsedTime: () => 'Elapsed time',
    mediaExitFullscreen: () => 'Minimize',
    mediaFullscreen: () => 'Fullscreen',
    mediaMute: () => 'Mute',
    mediaPause: () => 'Pause',
    mediaPlay: () => 'Play',
    mediaPlaybackRate: (opts) => `${opts.rate}x`,
    mediaPlaybackSpeed: () => 'Playback speed',
    mediaRemainingTime: () => 'Remaining time',
    mediaReplay: () => 'Replay',
    mediaScrubber: () => 'Seek',
    mediaTimeLabelled: (opts) => `${opts.label}: ${opts.duration}`,
    mediaTimeUnknown: () => 'not known yet',
    mediaTotalTime: () => 'Total time',
    mediaUnmute: () => 'Unmute',
    mediaVolume: () => 'Volume',
    mixed: () => 'Mixed',
    moveRowDown: () => 'Move down',
    moveRowToBottom: () => 'Move to bottom',
    moveRowToTop: () => 'Move to top',
    moveRowUp: () => 'Move up',
    pixels: (opts) => `${opts.value} pixels`,
    pixelsAtMax: (opts) => `${opts.value} pixels (max)`,
    pixelsAtMin: (opts) => `${opts.value} pixels (min)`,
    pressCommandArrowDownToOpen: () => 'Press Command + Down to open options menu',
    pressCtrlArrowDownToOpen: () => 'Press Ctrl + Down to open options menu',
    pressDeleteToDismiss: () => 'Press Delete to dismiss',
    remainingCharacters: () => 'Remaining characters',
    resizeHandle: () => 'Resize handle',
    select: () => 'Select an option',
    slider2dRoleDescription: () => '2D Slider',
    success: () => 'Success:',
    warning: () => 'Warning:',
    // ColorArea
    brightnessValue: (opts) => `Brightness: ${opts.value}%`,
    colorAreaHSB: (opts) => `Saturation: ${opts.saturation}%, Brightness: ${opts.brightness}%`,
    colorAreaHSL: (opts) => `Saturation: ${opts.saturation}%, Lightness: ${opts.lightness}%`,
    lightnessValue: (opts) => `Lightness: ${opts.value}%`,
    saturationValue: (opts) => `Saturation: ${opts.value}%`,
    // Keys
    backquote: () => 'Backquote',
    backspace: () => 'Backspace',
    bracketLeft: () => 'BracketLeft',
    bracketRight: () => 'BracketRight',
    comma: () => 'Comma',
    command: () => 'Command',
    control: () => 'Control',
    minus: () => 'Minus',
    option: () => 'Option',
    period: () => 'Period',
    plus: () => 'Plus',
    quote: () => 'Quote',
    semicolon: () => 'Semicolon',
    shift: () => 'Shift',
};
const _StringsContext = createContext(DEFAULT_STRINGS);
/**
 * Provide children with common string values via React context. The context value is an
 * object where each value is a function that returns a string.
 */
const StringsContext = _StringsContext.Provider;
/**
 * Grab the value of a specific string via React context.
 */
function useString(key, ...[opts]) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return useContext(_StringsContext)[key](opts);
}
/**
 * Grab an object of all available strings. Typically `useString` should be used,
 * but if you need to grab one of multiple strings, it can be more performant to grab all the
 * strings and then access just the one you need.
 */
function useAllStrings() {
    return useContext(_StringsContext);
}

export { DEFAULT_STRINGS, StringsContext, useAllStrings, useString };
//# sourceMappingURL=settings.strings.js.map
